import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import type { BillingRates, ModelPrice } from "@navibiz/chat-core";
import { api } from "./api";
import type {
  AdminUser,
  ApiKey,
  AuditRow,
  ConversationList,
  Gaps,
  Insights,
  KnowledgeState,
  KnowledgeVersion,
  LinkInsights,
  LinksState,
  Me,
  Overview,
  ProjectDetail,
  Report,
  Saldo,
  TestCase,
  TestRun,
  TestRunSummary,
  TranscriptMessage,
  ConversationRow,
  Usage,
} from "./types";

/** Query keys in one place, so a mutation invalidates exactly what it changed. */
export const keys = {
  me: ["me"] as const,
  overview: (days: number) => ["overview", days] as const,
  project: (id: string) => ["project", id] as const,
  knowledge: (id: string) => ["project", id, "knowledge"] as const,
  links: (id: string) => ["project", id, "links"] as const,
  linkInsights: (id: string, days: number) => ["project", id, "links", "insights", days] as const,
  versions: (id: string) => ["project", id, "versions"] as const,
  gaps: (id: string, days: number) => ["project", id, "gaps", days] as const,
  usage: (id: string, days: number) => ["project", id, "usage", days] as const,
  insights: (id: string, days: number) => ["project", id, "insights", days] as const,
  conversations: (id: string, params: string) => ["project", id, "conversations", params] as const,
  transcript: (id: string, cid: string) => ["project", id, "transcript", cid] as const,
  saldo: (id: string) => ["project", id, "saldo"] as const,
  keys: (id: string) => ["project", id, "keys"] as const,
  tests: (id: string) => ["project", id, "tests"] as const,
  testRun: (id: string, runId: string) => ["project", id, "test-run", runId] as const,
  report: (id: string, month: string) => ["project", id, "report", month] as const,
  billing: ["settings", "billing"] as const,
  admins: ["settings", "admins"] as const,
  audit: (projectId?: string) => ["settings", "audit", projectId ?? "all"] as const,
};

const p = (id: string) => `/projects/${id}`;

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api.get<Me>("/auth/me"), retry: false, staleTime: Infinity });

// Range switches keep the previous render on screen while the new range loads (no flash, no layout jump).
export const useOverview = (days: number, enabled = true) =>
  useQuery({ queryKey: keys.overview(days), queryFn: () => api.get<Overview>(`/overview?days=${days}`), placeholderData: keepPreviousData, enabled });
export const useProject = (id: string) => useQuery({ queryKey: keys.project(id), queryFn: () => api.get<ProjectDetail>(p(id)) });
export const useKnowledge = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.knowledge(id), queryFn: () => api.get<KnowledgeState>(`${p(id)}/knowledge`), enabled });
export const useLinks = (id: string, enabled = true) => useQuery({ queryKey: keys.links(id), queryFn: () => api.get<LinksState>(`${p(id)}/links`), enabled });
export const useLinkInsights = (id: string, days = 30) =>
  useQuery({ queryKey: keys.linkInsights(id, days), queryFn: () => api.get<LinkInsights>(`${p(id)}/links/insights?days=${days}`) });
export const useVersions = (id: string) => useQuery({ queryKey: keys.versions(id), queryFn: () => api.get<KnowledgeVersion[]>(`${p(id)}/knowledge/versions`) });
export const useGaps = (id: string, days = 30) => useQuery({ queryKey: keys.gaps(id, days), queryFn: () => api.get<Gaps>(`${p(id)}/knowledge/gaps?days=${days}`) });
export const useUsage = (id: string, days: number) =>
  useQuery({ queryKey: keys.usage(id, days), queryFn: () => api.get<Usage>(`${p(id)}/usage?days=${days}`), placeholderData: keepPreviousData });
export const useInsights = (id: string, days: number) =>
  useQuery({ queryKey: keys.insights(id, days), queryFn: () => api.get<Insights>(`${p(id)}/insights?days=${days}`), placeholderData: keepPreviousData });
export const useConversations = (id: string, params: URLSearchParams) =>
  useQuery({
    queryKey: keys.conversations(id, params.toString()),
    queryFn: () => api.get<ConversationList>(`${p(id)}/conversations?${params}`),
    placeholderData: keepPreviousData,
  });
export const useTranscript = (id: string, cid: string | null) =>
  useQuery({
    queryKey: keys.transcript(id, cid ?? ""),
    queryFn: () => api.get<{ conversation: ConversationRow; messages: TranscriptMessage[] }>(`${p(id)}/conversations/${cid}`),
    enabled: !!cid,
  });
export const useSaldo = (id: string) => useQuery({ queryKey: keys.saldo(id), queryFn: () => api.get<Saldo>(`${p(id)}/saldo`) });
export const useKeys = (id: string) => useQuery({ queryKey: keys.keys(id), queryFn: () => api.get<ApiKey[]>(`${p(id)}/keys`) });
export const useTests = (id: string) =>
  useQuery({ queryKey: keys.tests(id), queryFn: () => api.get<{ cases: TestCase[]; runs: TestRunSummary[] }>(`${p(id)}/tests`) });
export const useTestRun = (id: string, runId: string) => useQuery({ queryKey: keys.testRun(id, runId), queryFn: () => api.get<TestRun>(`${p(id)}/test-runs/${runId}`) });
export const useReport = (id: string, month: string) =>
  useQuery({ queryKey: keys.report(id, month), queryFn: () => api.get<Report>(`${p(id)}/report?month=${month}`), placeholderData: keepPreviousData });
export const useBilling = () => useQuery({ queryKey: keys.billing, queryFn: () => api.get<{ prices: ModelPrice[]; rates: BillingRates }>("/settings/billing") });
export const useAdmins = () => useQuery({ queryKey: keys.admins, queryFn: () => api.get<AdminUser[]>("/settings/admins") });
export const useAudit = (projectId?: string) =>
  useQuery({ queryKey: keys.audit(projectId), queryFn: () => api.get<AuditRow[]>(`/settings/audit${projectId ? `?projectId=${projectId}` : ""}`) });

/**
 * A mutation that invalidates the given keys on success. Every write in the
 * Console goes through this, so lists and totals never show stale numbers.
 */
export function useAction<TInput, TResult = unknown>(fn: (input: TInput) => Promise<TResult>, invalidate: QueryKey[] = []) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((queryKey) => client.invalidateQueries({ queryKey })));
    },
  });
}
